<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAiInsightsStore } from 'dashboard/stores/aiInsights';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import AgentForm from '../components/AgentForm.vue';

const { t } = useI18n();
const aiInsightsStore = useAiInsightsStore();

const showForm = ref(false);
const showDeleteModal = ref(false);
const editingAgent = ref(null);
const selectedAgent = ref(null);
const loading = ref({});

const agents = computed(() => aiInsightsStore.agents);
const uiFlags = computed(() => aiInsightsStore.uiFlags);

const tableHeaders = computed(() => [
  t('AI_INSIGHTS.AGENTS.LIST.TABLE_HEADER.NAME'),
  t('AI_INSIGHTS.AGENTS.LIST.TABLE_HEADER.DESCRIPTION'),
  t('AI_INSIGHTS.AGENTS.LIST.TABLE_HEADER.MODEL'),
  t('AI_INSIGHTS.AGENTS.LIST.TABLE_HEADER.STATUS'),
  t('AI_INSIGHTS.AGENTS.LIST.TABLE_HEADER.ACTIONS'),
]);

function openCreate() {
  editingAgent.value = null;
  showForm.value = true;
}

function openEdit(agent) {
  editingAgent.value = { ...agent };
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
  editingAgent.value = null;
}

function openDeletePopup(agent) {
  selectedAgent.value = agent;
  showDeleteModal.value = true;
}

function closeDeletePopup() {
  showDeleteModal.value = false;
  selectedAgent.value = null;
}

async function handleSave(payload) {
  try {
    if (editingAgent.value) {
      await aiInsightsStore.updateAgent(editingAgent.value.id, payload);
      useAlert(t('AI_INSIGHTS.AGENTS.FORM.API.SUCCESS_UPDATE'));
    } else {
      await aiInsightsStore.createAgent(payload);
      useAlert(t('AI_INSIGHTS.AGENTS.FORM.API.SUCCESS_CREATE'));
    }
    closeForm();
  } catch {
    useAlert(t('AI_INSIGHTS.AGENTS.FORM.API.ERROR'));
  }
}

async function confirmDeletion() {
  if (!selectedAgent.value) return;
  loading.value[selectedAgent.value.id] = true;
  closeDeletePopup();
  try {
    await aiInsightsStore.deleteAgent(selectedAgent.value.id);
    useAlert(t('AI_INSIGHTS.AGENTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('AI_INSIGHTS.AGENTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAgent.value.id] = false;
  }
}

async function toggleActive(agent) {
  loading.value[agent.id] = true;
  try {
    await aiInsightsStore.updateAgent(agent.id, { active: !agent.active });
  } finally {
    loading.value[agent.id] = false;
  }
}

onBeforeMount(() => aiInsightsStore.fetchAgents());
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingAgents"
    :loading-message="$t('AI_INSIGHTS.AGENTS.LOADING')"
    :no-records-found="!agents.length"
    :no-records-message="$t('AI_INSIGHTS.AGENTS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AI_INSIGHTS.AGENTS.HEADER')"
        :description="$t('AI_INSIGHTS.AGENTS.DESCRIPTION')"
      >
        <template v-if="agents.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AI_INSIGHTS.AGENTS.LIST.COUNT', { n: agents.length }) }}
          </span>
        </template>
        <template #actions>
          <Button :label="$t('AI_INSIGHTS.AGENTS.BUTTON.NEW')" size="sm" @click="openCreate" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="agents"
        :no-data-message="$t('AI_INSIGHTS.AGENTS.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="agent in items" :key="agent.id" :item="agent">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ agent.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ agent.description || '—' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-small text-n-slate-10">
                  {{ agent.model_override || $t('AI_INSIGHTS.AGENTS.LIST.MODEL_DEFAULT') }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="agent.active
                    ? 'bg-n-teal-2 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-10'"
                >
                  {{ agent.active
                    ? $t('AI_INSIGHTS.AGENTS.LIST.STATUS_ACTIVE')
                    : $t('AI_INSIGHTS.AGENTS.LIST.STATUS_INACTIVE') }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="agent.active
                      ? $t('AI_INSIGHTS.AGENTS.BUTTON.DEACTIVATE')
                      : $t('AI_INSIGHTS.AGENTS.BUTTON.ACTIVATE')"
                    :icon="agent.active ? 'i-ph-toggle-right' : 'i-ph-toggle-left'"
                    slate
                    sm
                    :is-loading="loading[agent.id]"
                    @click="toggleActive(agent)"
                  />
                  <Button
                    v-tooltip.top="$t('AI_INSIGHTS.AGENTS.BUTTON.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEdit(agent)"
                  />
                  <Button
                    v-tooltip.top="$t('AI_INSIGHTS.AGENTS.BUTTON.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showForm" :on-close="closeForm">
      <AgentForm :agent="editingAgent" @save="handleSave" @close="closeForm" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AI_INSIGHTS.AGENTS.DELETE.CONFIRM.TITLE')"
      :message="$t('AI_INSIGHTS.AGENTS.DELETE.CONFIRM.MESSAGE')"
      :message-value="selectedAgent?.name"
      :confirm-text="$t('AI_INSIGHTS.AGENTS.DELETE.CONFIRM.YES')"
      :reject-text="$t('AI_INSIGHTS.AGENTS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
